/**
 * CSVカラム編集テーブル
 *
 * CSV出力設定のカラム一覧編集
 */

import {
  Table,
  Group,
  ActionIcon,
  TextInput,
  Center,
  Stack,
  Text,
  Menu,
  ScrollArea,
  Popover,
  Alert,
} from "@mantine/core";
import { ArrowUp, ArrowDown, Trash2, ChevronDown, Info } from "lucide-react";
import { getFlattenPaths, getJsonValue } from "../../utils/jsonUtils";
import { CsvColumn } from "../../services/csvSettingsService";

// 値表示用ヘルパー
function formatDisplayValue(val: unknown): string {
  if (val === undefined || val === null) return "";
  if (typeof val === "string") return val;
  if (typeof val === "number") return String(val);
  if (typeof val === "boolean") return val ? "はい" : "いいえ";
  if (typeof val === "object") return JSON.stringify(val);
  return String(val);
}

function isValueEmpty(val: unknown): boolean {
  if (val === undefined || val === null) return true;
  if (typeof val === "string" && val.trim() === "") return true;
  return false;
}

function DisplayValueText({
  value,
  size = "sm",
  fw,
}: {
  value: unknown;
  size?: string;
  fw?: number;
}) {
  const isEmpty = isValueEmpty(value);
  const displayValue = formatDisplayValue(value);

  return (
    <Text
      size={size}
      c={isEmpty ? "dimmed" : undefined}
      fw={fw}
      style={{ wordBreak: "break-all" }}
    >
      {isEmpty ? "(空)" : displayValue}
    </Text>
  );
}

interface CsvColumnEditorProps {
  columns: CsvColumn[];
  analysisResult: Record<string, unknown> | null;
  onUpdateColumn: (
    index: number,
    updates: Partial<Omit<CsvColumn, "order">>
  ) => void;
  onRemoveColumn: (index: number) => void;
  onMoveColumn: (index: number, direction: "up" | "down") => void;
  onSetPath: (index: number, path: string) => void;
}

export function CsvColumnEditor({
  columns,
  analysisResult,
  onUpdateColumn,
  onRemoveColumn,
  onMoveColumn,
  onSetPath,
}: CsvColumnEditorProps) {
  return (
    <Table striped highlightOnHover withTableBorder>
      <Table.Thead>
        <Table.Tr>
          <Table.Th style={{ width: 60 }}>順序</Table.Th>
          <Table.Th>ヘッダー名</Table.Th>
          <Table.Th>AI読取項目</Table.Th>
          <Table.Th>デフォルト値</Table.Th>
          <Table.Th style={{ width: 80 }}>
            <Group gap={2}>
              必須
              <Popover width={280} position="bottom" withArrow shadow="md">
                <Popover.Target>
                  <ActionIcon
                    variant="transparent"
                    size="xs"
                    color="gray"
                    aria-label="詳細"
                  >
                    <Info size={14} />
                  </ActionIcon>
                </Popover.Target>
                <Popover.Dropdown>
                  <Stack gap="xs">
                    <Text size="xs" fw={700}>
                      必須チェック機能について
                    </Text>
                    <Text size="xs">
                      チェックを入れると、CSV生成時にこの項目の値が空欄だった場合、エラーとして通知されます。
                    </Text>
                    <Alert color="blue" variant="light" pt="xs" pb="xs">
                      <Text size="xs">
                        <strong>注意:</strong>{" "}
                        AIに対して「必ず読み取るように」と命令する機能ではありません。あくまで出力結果のチェック（バリデーション）を行うための設定です。
                      </Text>
                    </Alert>
                  </Stack>
                </Popover.Dropdown>
              </Popover>
            </Group>
          </Table.Th>
          <Table.Th style={{ width: 60 }}>操作</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {columns.map((col, index) => (
          <Table.Tr key={index}>
            <Table.Td>
              <Group gap={2}>
                <ActionIcon
                  size="xs"
                  variant="subtle"
                  onClick={() => onMoveColumn(index, "up")}
                  disabled={index === 0}
                >
                  <ArrowUp size={12} />
                </ActionIcon>
                <ActionIcon
                  size="xs"
                  variant="subtle"
                  onClick={() => onMoveColumn(index, "down")}
                  disabled={index === columns.length - 1}
                >
                  <ArrowDown size={12} />
                </ActionIcon>
              </Group>
            </Table.Td>
            <Table.Td>
              <TextInput
                size="xs"
                value={col.headerName}
                onChange={(e) =>
                  onUpdateColumn(index, {
                    headerName: e.currentTarget.value,
                  })
                }
                placeholder="会社名"
              />
            </Table.Td>
            <Table.Td>
              <TextInput
                size="xs"
                value={col.jsonPath}
                onChange={(e) =>
                  onUpdateColumn(index, {
                    jsonPath: e.currentTarget.value,
                  })
                }
                placeholder="右の▼ボタンから項目を選択"
                rightSection={
                  analysisResult && (
                    <Menu shadow="md" width={500} position="bottom-end">
                      <Menu.Target>
                        <ActionIcon size="xs" variant="subtle">
                          <ChevronDown size={14} />
                        </ActionIcon>
                      </Menu.Target>
                      <Menu.Dropdown>
                        <Menu.Label>解析結果から選択</Menu.Label>
                        <ScrollArea.Autosize mah={600}>
                          {getFlattenPaths(analysisResult).map((p, i) => (
                            <Menu.Item
                              key={i}
                              onClick={() => onSetPath(index, p.path)}
                              p="sm"
                            >
                              <Stack gap={4}>
                                <Text size="sm" fw={700}>
                                  {p.path}
                                </Text>
                                <DisplayValueText value={p.value} />
                              </Stack>
                            </Menu.Item>
                          ))}
                        </ScrollArea.Autosize>
                      </Menu.Dropdown>
                    </Menu>
                  )
                }
              />
              {/* 実際の値を表示 */}
              {analysisResult && col.jsonPath && (
                <Group gap={4} mt={2}>
                  <Text size="10px" c="dimmed">
                    値:
                  </Text>
                  <DisplayValueText
                    value={getJsonValue(analysisResult, col.jsonPath)}
                    size="10px"
                    fw={700}
                  />
                </Group>
              )}
            </Table.Td>
            <Table.Td>
              <TextInput
                size="xs"
                value={col.defaultValue || ""}
                onChange={(e) =>
                  onUpdateColumn(index, {
                    defaultValue: e.currentTarget.value,
                  })
                }
              />
            </Table.Td>
            <Table.Td>
              <Center>
                <input
                  type="checkbox"
                  checked={col.required}
                  onChange={(e) =>
                    onUpdateColumn(index, {
                      required: e.target.checked,
                    })
                  }
                />
              </Center>
            </Table.Td>
            <Table.Td>
              <ActionIcon
                color="red"
                variant="subtle"
                size="sm"
                onClick={() => onRemoveColumn(index)}
              >
                <Trash2 size={14} />
              </ActionIcon>
            </Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  );
}
